import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { PageHeader, Loading, StatusBadge, money, moneyShort } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Users, Radio, PhoneCall, Coffee, Clock, AlertTriangle, ListTodo, Ticket, Wallet, TrendingUp, UserPlus, Zap, ChevronRight,
} from "lucide-react";

const TILES = [
  { key: "team_online", label: "Online", icon: Users, tone: "text-success" },
  { key: "on_shift", label: "On Shift", icon: Radio, tone: "text-primary" },
  { key: "active_calls", label: "Active Calls", icon: PhoneCall, tone: "text-info" },
  { key: "wrap_up", label: "Wrap-up", icon: Coffee, tone: "text-automation" },
  { key: "sla_at_risk", label: "SLA At Risk", icon: Clock, tone: "text-warning", drill: "at_risk" },
  { key: "sla_breached", label: "SLA Breached", icon: AlertTriangle, tone: "text-critical", drill: "breached" },
  { key: "pending_tasks", label: "Pending Tasks", icon: ListTodo, tone: "text-escalation", drill: "tasks" },
  { key: "open_tickets", label: "Open Tickets", icon: Ticket, tone: "text-info", drill: "tickets" },
  { key: "escalations", label: "Escalations", icon: AlertTriangle, tone: "text-critical" },
  { key: "outstanding", label: "Outstanding", icon: Wallet, tone: "text-warning", fmt: moneyShort, drill: "overdue" },
  { key: "revenue", label: "Revenue", icon: TrendingUp, tone: "text-success", fmt: moneyShort },
  { key: "new_leads", label: "New Leads", icon: UserPlus, tone: "text-primary" },
];

export default function WarRoom() {
  const navigate = useNavigate();
  const [drill, setDrill] = useState(null);
  const { data, isLoading } = useQuery({ queryKey: ["war-room"], queryFn: async () => (await api.get("/war-room")).data, refetchInterval: 12000 });

  if (isLoading || !data) return <div><PageHeader title="War Room" /><Loading rows={8} /></div>;
  const m = data.metrics;

  const drillData = {
    breached: { title: "SLA Breached", items: data.support.breached, render: (t) => `${t.subject} · ${t.customer_name}` },
    tickets: { title: "Open Tickets", items: data.support.open, render: (t) => `${t.subject} · ${t.priority}` },
    tasks: { title: "Pending Tasks", items: data.operations.tasks, render: (t) => `${t.title} · ${t.status}` },
    overdue: { title: "Overdue Invoices", items: data.accounts.overdue, render: (i) => `${i.invoice_number} · ${money(i.total)}` },
    at_risk: { title: "Deals At Risk", items: data.sales.deals_at_risk, render: (o) => `${o.title} · ${money(o.value)}` },
  };

  return (
    <div>
      <PageHeader title={<span className="flex items-center gap-2"><Zap className="h-6 w-6 text-primary" /> War Room</span>}
        subtitle="Live organization-wide operational command center" testid="warroom-header">
        <Badge className="bg-success/10 text-success hover:bg-success/10"><span className="mr-1.5 h-1.5 w-1.5 animate-pulse rounded-full bg-success" /> Live · updates every 12s</Badge>
      </PageHeader>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        {TILES.map((t, i) => {
          const val = t.fmt ? t.fmt(m[t.key]) : m[t.key];
          const clickable = !!t.drill;
          return (
            <motion.button key={t.key} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}
              onClick={() => clickable && setDrill(t.drill)} disabled={!clickable}
              className={`rounded-xl border border-border bg-card p-3 text-left transition-colors ${clickable ? "cursor-pointer hover:border-primary/50" : "cursor-default"}`}
              data-testid={`warroom-tile-${t.key}`}>
              <div className="flex items-center justify-between">
                <t.icon className={`h-4 w-4 ${t.tone}`} />
                {clickable && <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />}
              </div>
              <p className="mt-2 font-heading text-xl font-bold">{val}</p>
              <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{t.label}</p>
            </motion.button>
          );
        })}
      </div>

      <div className="mt-6 grid gap-4 lg:grid-cols-2">
        <Card className="p-5">
          <h3 className="mb-3 font-heading text-base font-semibold">Live Team</h3>
          <div className="space-y-2">
            {data.live_team.slice(0, 8).map((u) => (
              <div key={u.id} className="flex items-center justify-between text-sm">
                <span className="flex items-center gap-2">
                  <span className={`h-2 w-2 rounded-full ${u.live_status === "offline" ? "bg-muted-foreground" : "bg-success"}`} />
                  {u.name}
                </span>
                <span className="text-xs capitalize text-muted-foreground">{u.live_status?.replace("_", " ")}</span>
              </div>
            ))}
          </div>
        </Card>

        <Card className="p-5">
          <h3 className="mb-3 font-heading text-base font-semibold">Bottleneck Detection</h3>
          <div className="space-y-2">
            {data.operations.bottlenecks.map((b) => (
              <div key={b.department} className="flex items-center justify-between rounded-lg border border-border p-3" data-testid={`bottleneck-${b.department}`}>
                <div>
                  <p className="text-sm font-medium">{b.department}</p>
                  <p className="text-xs text-muted-foreground">{b.pending} pending · avg {b.avg_hours}h vs SLA {b.sla_hours}h</p>
                </div>
                <StatusBadge value={b.status === "bottleneck" ? "breached" : b.status === "watch" ? "at_risk" : "healthy"} />
              </div>
            ))}
            {data.operations.bottlenecks.length === 0 && <p className="py-4 text-center text-xs text-muted-foreground">No operational data.</p>}
          </div>
        </Card>

        <Card className="p-5">
          <h3 className="mb-3 font-heading text-base font-semibold">Deals at risk</h3>
          <div className="space-y-2">
            {data.sales.deals_at_risk.map((o) => (
              <div key={o.id} className="flex items-center justify-between text-sm">
                <span className="truncate">{o.title}</span><span className="font-medium text-warning">{moneyShort(o.value)}</span>
              </div>
            ))}
            {data.sales.deals_at_risk.length === 0 && <p className="py-4 text-center text-xs text-muted-foreground">All healthy 🎉</p>}
          </div>
        </Card>

        <Card className="p-5">
          <h3 className="mb-3 font-heading text-base font-semibold">Accounts · Overdue</h3>
          <div className="space-y-2">
            {data.accounts.overdue.map((i) => (
              <div key={i.id} className="flex items-center justify-between text-sm">
                <span>{i.invoice_number} · {i.customer_name}</span><span className="font-medium text-critical">{moneyShort(i.total - (i.amount_paid || 0))}</span>
              </div>
            ))}
            {data.accounts.overdue.length === 0 && <p className="py-4 text-center text-xs text-muted-foreground">No overdue invoices.</p>}
          </div>
        </Card>
      </div>

      <Dialog open={!!drill} onOpenChange={(o) => !o && setDrill(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{drill && drillData[drill]?.title}</DialogTitle></DialogHeader>
          <div className="max-h-96 space-y-2 overflow-y-auto">
            {drill && drillData[drill]?.items.length === 0 && <p className="py-6 text-center text-sm text-muted-foreground">Nothing here right now.</p>}
            {drill && drillData[drill]?.items.map((it, i) => (
              <div key={i} className="flex items-center justify-between rounded-lg border border-border p-3 text-sm" data-testid={`drill-item-${i}`}>
                <span className="truncate">{drillData[drill].render(it)}</span>
                {it.sla_status && <StatusBadge value={it.sla_status} />}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
